import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';

/**
 * Traveler or partner WhatsApp opt-in card.
 * mode: 'traveler' | 'partner'
 */
const WhatsAppOptInCard = ({ mode = 'traveler' }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [phone, setPhone] = useState('');
  const [identity, setIdentity] = useState(null);
  const [configured, setConfigured] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [whatsappChat, setWhatsappChat] = useState(true);
  const [whatsappRequests, setWhatsappRequests] = useState(true);
  const [partnerOptIn, setPartnerOptIn] = useState(false);

  const isPartner = mode === 'partner';
  const statusPath = isPartner ? '/whatsapp/partner/status' : '/whatsapp/status';
  const optInPath = isPartner ? '/whatsapp/partner/opt-in' : '/whatsapp/opt-in';
  const optOutPath = isPartner ? '/whatsapp/partner/opt-out' : '/whatsapp/opt-out';

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const authConfig = isPartner ? { usePartnerAuth: true } : {};
      const res = await api.get(statusPath, authConfig);
      setEnabled(Boolean(res.data?.enabled));
      setConfigured(Boolean(res.data?.configured));
      if (isPartner) {
        setPhone(res.data?.partnerUser?.phone || res.data?.identity?.phone_e164 || '');
        setPartnerOptIn(Boolean(res.data?.partnerUser?.whatsapp_opt_in));
        setIdentity(res.data?.identity || null);
      } else {
        setPhone(res.data?.phone || res.data?.identity?.phone_e164 || '');
        setIdentity(res.data?.identity || null);
        setWhatsappChat(res.data?.preferences?.whatsapp_chat !== false);
        setWhatsappRequests(res.data?.preferences?.whatsapp_requests !== false);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('whatsapp.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [isPartner, statusPath, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleOptIn = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const authConfig = isPartner ? { usePartnerAuth: true } : {};
      const body = isPartner
        ? { phone }
        : { phone, whatsapp_chat: whatsappChat, whatsapp_requests: whatsappRequests };
      const res = await api.post(optInPath, body, authConfig);
      setSuccess(res.data?.message || t('whatsapp.optInSent'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('whatsapp.optInFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleOptOut = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const authConfig = isPartner ? { usePartnerAuth: true } : {};
      await api.post(optOutPath, {}, authConfig);
      setSuccess(t('whatsapp.optOutDone'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('whatsapp.optOutFailed'));
    } finally {
      setSaving(false);
    }
  };

  const optedIn = identity?.opt_in_status === 'opted_in'
    || (isPartner && partnerOptIn && identity?.opt_in_status === 'opted_in');
  const pending = identity?.opt_in_status === 'pending';

  if (!loading && !enabled) {
    return null;
  }

  return (
    <Box
      sx={{
        border: `1px solid ${DOCUMENT.ruleHard}`,
        bgcolor: DOCUMENT.surface,
        p: 2.5,
        mb: 2,
      }}
    >
      <Typography
        sx={{
          fontFamily: DOCUMENT.display,
          fontSize: 22,
          color: DOCUMENT.ink,
          mb: 0.75,
        }}
      >
        {t('whatsapp.title')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14, mb: 2, maxWidth: '62ch' }}>
        {t('whatsapp.subtitle')}
      </Typography>

      {!configured ? (
        <Alert severity="info" sx={{ mb: 2, borderRadius: 0 }}>
          {t('whatsapp.notConfigured')}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} sx={{ color: DOCUMENT.oxblood }} />
        </Box>
      ) : (
        <>
          {error ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
              {error}
            </Alert>
          ) : null}
          {success ? (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          ) : null}

          <TextField
            fullWidth
            label={t('whatsapp.phoneLabel')}
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+9715xxxxxxx"
            helperText={t('whatsapp.phoneHint')}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />

          {mode === 'traveler' ? (
            <Box sx={{ mb: 1.5 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={whatsappChat}
                    onChange={(e) => setWhatsappChat(e.target.checked)}
                    color="primary"
                  />
                )}
                label={t('whatsapp.prefChat')}
              />
              <FormControlLabel
                control={(
                  <Switch
                    checked={whatsappRequests}
                    onChange={(e) => setWhatsappRequests(e.target.checked)}
                    color="primary"
                  />
                )}
                label={t('whatsapp.prefRequests')}
              />
            </Box>
          ) : null}

          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>
            {optedIn
              ? t('whatsapp.statusOptedIn')
              : pending
                ? t('whatsapp.statusPending')
                : t('whatsapp.statusOff')}
            {identity?.phone_e164 ? ` · ${identity.phone_e164}` : ''}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              disableElevation
              disabled={saving || !phone.trim()}
              onClick={handleOptIn}
              sx={{
                bgcolor: DOCUMENT.oxblood,
                borderRadius: 0,
                textTransform: 'none',
                '&:hover': { bgcolor: DOCUMENT.oxblood },
              }}
            >
              {saving ? <CircularProgress size={18} color="inherit" /> : t('whatsapp.enable')}
            </Button>
            {(optedIn || pending) ? (
              <Button
                disabled={saving}
                onClick={handleOptOut}
                sx={{ textTransform: 'none', borderRadius: 0, color: DOCUMENT.oxblood }}
              >
                {t('whatsapp.disable')}
              </Button>
            ) : null}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WhatsAppOptInCard;
